<template>
  <div class="content-news-slider">
    <div class="grid-container">
      <div class="inner">
        <div class="titlewrapper">
          <h2 v-if="data.title" class="title" v-html="data.title" />
          <Link v-if="data.link" :link="data.link" class="button-primary button" icon-position="left" />
        </div>
        <div class="newsteaser-wrapper" :class="{ '-single-card': data.news.length === 1 }">
          <NewsTeaser v-for="it in data.news" :key="it.id" :data="it" class="item" />
          <div class="pattern" />
        </div>
      </div>
    </div>
    <Stairs class="stairs" />
  </div>
</template>

<script setup lang="ts">
import type { ContentNewssliderFragment } from '#gql';
import NewsTeaser from '~/components/content-modules/components/NewsTeaser.vue';
import Link from '~/components/content-modules/components/Link.vue';
import Stairs from '~/components/header-modules/svgs/Stairs.vue';

defineProps<{
  data: ContentNewssliderFragment;
}>();
</script>

<style scoped lang="scss">
.content-news-slider {
  background-color: var(--color-forest-light);
  margin: var(--spacing-xs) 0;
  width: 100%;
  position: relative;

  @media (--xs), (--sm) {
    padding: var(--spacing-2xl) 0;
  }

  @media (--md), (--lg) {
    padding: var(--spacing-4xl) 0;
  }

  > .stairs {
    position: absolute;
    inset: 0;
    transform: rotate(180deg);

    @media (--xs), (--sm) {
      display: none;
    }

    @media (--md) {
      width: 10rem;
      aspect-ratio: 1 / 1;
    }

    @media (--lg) {
      width: 17.5rem;
      aspect-ratio: 1 / 1;
    }

    @media (--lg-custom) {
      width: 26.25rem;
      aspect-ratio: 1 / 1;
    }
  }

  > .grid-container {
    @include grid-layout();

    & {
      padding: var(--site-inner-padding);
      width: 100%;
      max-width: var(--site-max-width);
      margin: auto;
    }

    > .inner {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: subgrid;

      @media (--sm) {
        grid-column: 2 / 24;
      }

      @media (--xs), (--sm) {
        display: flex;
        flex-direction: column;
      }

      > .titlewrapper {
        color: var(--color-forest-dark);
        position: relative;
        z-index: 1;

        @media (--md), (--lg) {
          grid-column: 5 / 11;
          align-self: center;
        }

        @media (--xs), (--sm) {
          margin-bottom: var(--spacing-sm);
        }

        :deep(.button) {
          color: var(--color-forest-dark);

          @media (hover: hover) and (pointer: fine) {
            &:hover > .iconwrapper {
              background: var(--color-sky);
            }
          }

          > .iconwrapper {
            background: var(--color-forest-dark);
          }
        }

        > .title {
          color: var(--color-forest-dark);

          @media (--xs), (--sm) {
            margin-bottom: var(--spacing-3xs);
          }

          @media (--md), (--lg) {
            margin-bottom: var(--spacing-2xs);
          }
        }
      }

      > .newsteaser-wrapper {
        position: relative;
        z-index: 1;
        gap: var(--spacing-xs);

        &.-single-card {
          grid-column: 12 / 19;
          grid-template-columns: 1fr;
        }

        @media (--xs), (--sm) {
          display: flex;
          flex-direction: column;
          grid-column: 1 / -1;
        }

        @media (--md), (--lg) {
          grid-column: 12 / -1;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }

        > .pattern {
          position: absolute;
          z-index: -1;
          background: url('/design-elements/pattern.svg');
          width: 100%;
          height: 100%;

          @media (--xs), (--sm) {
            bottom: -1.5rem;
            right: -1.5rem;
          }

          @media (--md), (--lg) {
            bottom: -3rem;
            left: -3rem;
          }
        }
      }
    }
  }
}
</style>
